import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Phone, ChevronRight, Key, User, Eye, EyeOff, Hash,
  Home, FileText, Wrench, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { tenantAPI, agencySettingsAPI } from '../lib/api';
import AuthLayout from '../components/auth/AuthLayout';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const TENANT_ACCENT = '#10B981';  // Emerald

const TenantLogin = () => {
  const [loginMethod, setLoginMethod] = useState('credentials');
  const [step, setStep] = useState(1);
  const [agencySettings, setAgencySettings] = useState(null);

  const [phone, setPhone] = useState('');
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [pinUsername, setPinUsername] = useState('');
  const [pinCode, setPinCode] = useState('');

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    agencySettingsAPI.getPublic().then(r => setAgencySettings(r.data)).catch(() => {});
  }, []);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phone) return toast.error('Numero de telephone requis');
    setLoading(true);
    try {
      const response = await tenantAPI.getAvailableProperties(phone);
      const ap = response.data.properties;
      if (ap.length === 0) return toast.error('Aucun logement trouve pour ce numero');
      setProperties(ap);
      if (ap.length === 1) setSelectedProperty(ap[0].id);
      setStep(2);
    } catch { toast.error('Erreur de verification'); }
    finally { setLoading(false); }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    if (!selectedProperty) return toast.error('Selectionner un logement');
    setLoading(true);
    try {
      const r = await tenantAPI.login({ phone, property_id: selectedProperty });
      localStorage.setItem('tenant_token', r.data.access_token);
      localStorage.setItem('tenant_data', JSON.stringify({ tenant: r.data.tenant, property: r.data.property }));
      toast.success('Connexion reussie');
      navigate('/tenant/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally { setLoading(false); }
  };

  const handleCredentialsLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) return toast.error('Veuillez remplir tous les champs');
    setLoading(true);
    try {
      const r = await tenantAPI.loginWithCredentials({ username, password });
      localStorage.setItem('tenant_token', r.data.access_token);
      localStorage.setItem('tenant_data', JSON.stringify({ tenant: r.data.tenant, property: r.data.property }));
      toast.success('Connexion reussie');
      navigate('/tenant/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiant ou mot de passe incorrect');
    } finally { setLoading(false); }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    if (!pinUsername || !pinCode) return toast.error('Identifiant et code PIN requis');
    if (pinCode.length !== 4) return toast.error('Le code PIN doit contenir 4 chiffres');
    setLoading(true);
    try {
      const r = await tenantAPI.loginWithPin({ username: pinUsername, pin_code: pinCode });
      localStorage.setItem('tenant_token', r.data.access_token);
      localStorage.setItem('tenant_data', JSON.stringify({ tenant: r.data.tenant, property: r.data.property }));
      toast.success('Connexion reussie');
      navigate('/tenant/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiant ou code PIN incorrect');
    } finally { setLoading(false); }
  };

  const logo = agencySettings?.logo_url && agencySettings.logo_url.length > 0
    ? (agencySettings.logo_url.startsWith('http')
        ? agencySettings.logo_url
        : `${process.env.REACT_APP_BACKEND_URL}${agencySettings.logo_url}`)
    : null;

  return (
    <>
      <AuthLayout
        accentColor={TENANT_ACCENT}
        accentLabel="Portail Locataire"
        title="Tout votre logement, en un seul espace"
        tagline="Consultez vos quittances, payez votre loyer en Mobile Money, signalez une panne et restez informe en temps reel."
        benefits={[
          { icon: FileText, label: 'Telecharger toutes vos quittances et l\'attestation de domicile' },
          { icon: Home, label: 'Payer votre loyer en Mobile Money (Jɛmɛnipay, Orange Money)' },
          { icon: Wrench, label: 'Signaler une reparation avec photos — suivi en direct' },
        ]}
        agencyLogoUrl={logo}
        agencyName={agencySettings?.name || "GAB'S SERVICES HABITAT"}
        otherPortals={[
          { to: '/login', label: 'Espace Agent', sub: 'Admin / Personnel' },
          { to: '/owner/login', label: 'Espace Proprietaire', sub: 'Soldes, retraits...' },
        ]}
      >
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1B3A4B]">Acceder a mon espace</h2>
          <p className="text-sm text-gray-500 mt-1">Choisissez votre methode de connexion</p>
        </div>

        <Tabs value={loginMethod} onValueChange={setLoginMethod}>
          <TabsList className="grid w-full grid-cols-3 mb-6 bg-gray-100/70 p-1 h-11">
            <TabsTrigger value="credentials" data-testid="tab-credentials" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Key className="w-3.5 h-3.5 mr-1.5" /> Identifiant
            </TabsTrigger>
            <TabsTrigger value="pin" data-testid="tab-pin" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Hash className="w-3.5 h-3.5 mr-1.5" /> Code PIN
            </TabsTrigger>
            <TabsTrigger value="phone" data-testid="tab-phone" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Phone className="w-3.5 h-3.5 mr-1.5" /> Telephone
            </TabsTrigger>
          </TabsList>

          {/* Credentials */}
          <TabsContent value="credentials">
            <form onSubmit={handleCredentialsLogin} className="space-y-5">
              <FieldUser id="username" label="Identifiant" value={username} setValue={setUsername} accent={TENANT_ACCENT} dt="tenant-username-input" />
              <FieldPassword id="password" label="Mot de passe" value={password} setValue={setPassword}
                              showPassword={showPassword} setShowPassword={setShowPassword} accent={TENANT_ACCENT} dt="tenant-password-input" />
              <SubmitBtn loading={loading} dt="tenant-credentials-submit-btn">Se connecter</SubmitBtn>
            </form>
          </TabsContent>

          {/* PIN */}
          <TabsContent value="pin">
            <form onSubmit={handlePinLogin} className="space-y-5">
              <FieldUser id="pin-username" label="Identifiant" value={pinUsername} setValue={setPinUsername} accent={TENANT_ACCENT} dt="tenant-pin-username-input" />
              <div className="space-y-2">
                <Label htmlFor="pin-code" className="text-xs font-semibold uppercase tracking-wider text-gray-600">Code PIN (4 chiffres)</Label>
                <Input
                  id="pin-code"
                  type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={4}
                  placeholder="0000"
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="text-center text-3xl tracking-[0.6em] font-mono h-14 bg-gray-50 border-gray-200 focus:bg-white"
                  style={{ borderColor: pinCode.length === 4 ? TENANT_ACCENT : undefined }}
                  data-testid="tenant-pin-input"
                />
                <p className="text-[11px] text-gray-500 text-center">Code a 4 chiffres fourni par votre agence</p>
              </div>
              <SubmitBtn loading={loading || pinCode.length !== 4} dt="tenant-pin-submit-btn">Se connecter</SubmitBtn>
            </form>
          </TabsContent>

          {/* Phone */}
          <TabsContent value="phone">
            {step === 1 ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-gray-600">Numero de telephone</Label>
                  <div className="relative group">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-emerald-500" />
                    <Input
                      id="phone" type="tel" placeholder="+223 70 00 00 00"
                      value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                      data-testid="tenant-phone-input"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500">Numero enregistre lors de votre contrat</p>
                </div>
                <SubmitBtn loading={loading} dt="tenant-phone-submit-btn">
                  <>Continuer <ChevronRight className="w-4 h-4 ml-1" /></>
                </SubmitBtn>
              </form>
            ) : (
              <form onSubmit={handlePhoneLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Logement</Label>
                  <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                    <SelectTrigger data-testid="tenant-property-select" className="h-12 bg-gray-50 border-gray-200">
                      <SelectValue placeholder="Selectionner un logement" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{p.title}</span>
                            <span className="text-xs text-gray-500">{p.address}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-12">Retour</Button>
                  <SubmitBtn loading={loading} dt="tenant-login-submit-btn" className="flex-1">Acceder</SubmitBtn>
                </div>
              </form>
            )}
          </TabsContent>
        </Tabs>
      </AuthLayout>
      <Toaster position="top-right" richColors />
    </>
  );
};

const FieldUser = ({ id, label, value, setValue, accent, dt }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-gray-600">{label}</Label>
    <div className="relative group">
      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-colors" style={{ '--accent': accent }} />
      <Input
        id={id} type="text" placeholder="Votre identifiant"
        value={value} onChange={(e) => setValue(e.target.value)}
        className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white"
        style={{ '--tw-ring-color': accent }}
        data-testid={dt}
      />
    </div>
  </div>
);

const FieldPassword = ({ id, label, value, setValue, showPassword, setShowPassword, dt }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-gray-600">{label}</Label>
    <div className="relative group">
      <Key className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <Input
        id={id} type={showPassword ? 'text' : 'password'} placeholder="••••••••"
        value={value} onChange={(e) => setValue(e.target.value)}
        className="pl-11 pr-11 h-12 bg-gray-50 border-gray-200 focus:bg-white"
        data-testid={dt}
      />
      <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1B3A4B]" tabIndex={-1}>
        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

const SubmitBtn = ({ children, loading, dt, className = '' }) => (
  <Button
    type="submit" disabled={loading}
    className={`h-12 bg-[#1B3A4B] hover:bg-[#142D3A] text-white font-semibold relative overflow-hidden group ${className} ${className.includes('flex-1') ? '' : 'w-full'}`}
    data-testid={dt}
  >
    {loading ? (
      <span className="flex items-center gap-2">
        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        Connexion...
      </span>
    ) : (
      <span className="flex items-center justify-center gap-2">
        {children}
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    )}
    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-400" />
  </Button>
);

export default TenantLogin;
